import React, { useMemo, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { ExternalLink, User, GripVertical } from "lucide-react";

const STATUSES = ["To-Do", "Editing Pending", "Publishing Pending", "Review Pending", "Published"];
const STATUS_META = {
  "To-Do": { bg: "bg-slate-50", border: "border-slate-200", dot: "bg-slate-400" },
  "Editing Pending": { bg: "bg-amber-50", border: "border-amber-200", dot: "bg-amber-500" },
  "Publishing Pending": { bg: "bg-orange-50", border: "border-orange-200", dot: "bg-orange-500" },
  "Review Pending": { bg: "bg-blue-50", border: "border-blue-200", dot: "bg-blue-500" },
  "Published": { bg: "bg-emerald-50", border: "border-emerald-200", dot: "bg-emerald-500" },
};

// Iter 42.8 — Column virtualization. Each kanban column now paints only the
// ~15 cards currently in its viewport via @tanstack/react-virtual. Combined
// with the ScopeView bounded fetch (300 tasks), the tab stays smooth even
// when a department has 100k tasks — DOM stays under ~500 nodes regardless.
function BoardColumn({ status, cards, userMap, projMap, showProject, onDragStart, onDropTo, titleKey }) {
  const meta = STATUS_META[status] || { bg: "bg-slate-50", border: "border-slate-200", dot: "bg-slate-400" };
  const scrollRef = useRef(null);
  const virt = useVirtualizer({
    count: cards.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 120, // card body + margin
    overscan: 6,
    getItemKey: (i) => cards[i]?.id ?? i,
  });
  const items = virt.getVirtualItems();
  const totalHeight = virt.getTotalSize();
  const padTop = items.length ? items[0].start : 0;
  const padBot = items.length ? totalHeight - items[items.length - 1].end : 0;

  return (
    <div
      className={`w-72 shrink-0 rounded-xl border ${meta.border} ${meta.bg} flex flex-col max-h-full`}
      data-testid={`board-column-${status}`}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => onDropTo(e, status)}
    >
      <div className="p-3 flex items-center gap-2 sticky top-0 bg-inherit rounded-t-xl border-b border-slate-200/70 z-[1]">
        <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
        <span className="font-display font-semibold text-sm text-slate-800">{status}</span>
        <span className="ml-auto text-xs font-medium text-slate-500 bg-white/80 px-2 py-0.5 rounded-full">
          {cards.length}
        </span>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin p-3">
        {cards.length === 0 && (
          <div className="text-xs text-slate-400 italic text-center py-8">Drop tasks here</div>
        )}
        {padTop > 0 && <div aria-hidden style={{ height: padTop }} />}
        {items.map((vr) => {
          const t = cards[vr.index];
          if (!t) return null;
          return (
            <Card
              key={t.id}
              draggable
              onDragStart={(e) => onDragStart(e, t)}
              className="p-3 bg-white border-slate-200 hover:border-purple-300 hover:shadow-sm transition cursor-grab active:cursor-grabbing group mb-2"
              data-testid={`board-card-${t.id}`}
            >
              <div className="flex items-start gap-1.5">
                <GripVertical className="h-3.5 w-3.5 text-slate-300 mt-0.5 opacity-0 group-hover:opacity-100" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 line-clamp-2">{t[titleKey] || t.keyword || "(empty)"}</div>
                  {showProject && projMap[t.project_id] && (
                    <Link to={`/projects/${t.project_id}`} className="mt-1 inline-block text-[11px] text-purple-600 hover:underline">
                      {projMap[t.project_id].name}
                    </Link>
                  )}
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {t.niche && <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-slate-50">{t.niche}</Badge>}
                    {t.method && <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-purple-50 text-purple-700 border-purple-200">{t.method}</Badge>}
                  </div>
                  {(t.writer_id || t.publisher_id) && (
                    <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500">
                      {t.writer_id && (
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span>{userMap[t.writer_id]?.name || "—"}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {t.docs && (
                    <a href={t.docs} target="_blank" rel="noreferrer" className="mt-2 text-[11px] text-purple-600 hover:underline flex items-center gap-1">
                      <ExternalLink className="h-2.5 w-2.5" /> Open docs
                    </a>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
        {padBot > 0 && <div aria-hidden style={{ height: padBot }} />}
      </div>
    </div>
  );
}

export default function BoardView({ tasks, users, projects, onStatusChange, showProject = false, titleKey = "keyword", statuses = STATUSES }) {
  const userMap = useMemo(() => Object.fromEntries((users || []).map((u) => [u.id, u])), [users]);
  const projMap = useMemo(() => Object.fromEntries((projects || []).map((p) => [p.id, p])), [projects]);

  const byStatus = useMemo(() => {
    const groups = Object.fromEntries(statuses.map((s) => [s, []]));
    tasks.forEach((t) => { (groups[t.status] || groups[statuses[0]]).push(t); });
    return groups;
  }, [tasks, statuses]);

  const onDragStart = (e, task) => {
    e.dataTransfer.setData("text/plain", task.id);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDropTo = (e, status) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    if (!id) return;
    const t = tasks.find((x) => x.id === id);
    if (t && t.status !== status && onStatusChange) onStatusChange(id, status);
  };

  return (
    <div className="flex-1 overflow-auto scrollbar-thin bg-slate-50" data-testid="board-view">
      <div className="flex gap-4 p-4 h-full min-w-max">
        {statuses.map((status) => (
          <BoardColumn
            key={status}
            status={status}
            cards={byStatus[status] || []}
            userMap={userMap}
            projMap={projMap}
            showProject={showProject}
            onDragStart={onDragStart}
            onDropTo={onDropTo}
            titleKey={titleKey}
          />
        ))}
      </div>
    </div>
  );
}
